import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createMarketplaceQueryCache } from 'components/marketplace/queryCache';
import type { ChosenSlot, TourHome } from 'components/tours/TourBookingSteps';
import { useWebTourBookingGate } from 'components/tours/webTourBookingFlag';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import type { MarketplaceProperty } from '../types';
import type { SelfTourAppHandoffPlacement } from './SelfTourAppHandoffModal';
import { SelfTourAppHandoffModal } from './SelfTourAppHandoffModal';

export type SelfTourHomeSource = Pick<
  MarketplaceProperty,
  'street' | 'city' | 'state' | 'photosSm'
>;

export const selfTourHome = (property: SelfTourHomeSource): TourHome | null => {
  if (!property.street) {
    return null;
  }
  const locality = [property.city, property.state].filter(Boolean).join(', ');
  return {
    street: property.street,
    locality: locality || null,
    photoUrl: property.photosSm[0] ?? null,
  };
};

const TourBookingSheet = dynamic(() =>
  import('components/tours/TourBookingSheet').then((module) => module.TourBookingSheet),
);

// The PDP render tree has no QueryClientProvider of its own (only /homes and
// /nl-search install one), so the self-tour surfaces bring theirs, wired to the
// marketplace QueryCache so query errors still reach Sentry. Everything below,
// including the flag query, must render inside it.
export const SelfTourQueryClientProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        queryCache: createMarketplaceQueryCache(),
        defaultOptions: { queries: { retry: false } },
      }),
    [],
  );
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

type SelfTourDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Called when a login-return replay wants the booking sheet opened. */
  onOpenRequest: () => void;
  placement: SelfTourAppHandoffPlacement;
  addressToken?: string;
  home?: TourHome | null;
  slug?: string;
  tourBookingListingId?: string | null;
  initialSlot?: ChosenSlot;
};

export const SelfTourDialogSurface = ({
  isOpen,
  onClose,
  onOpenRequest,
  placement,
  addressToken,
  home,
  slug,
  tourBookingListingId,
  initialSlot,
}: SelfTourDialogProps) => {
  const { authentication } = useAuth();
  const customerUuid =
    authentication.state === 'authenticating'
      ? undefined
      : (authentication.user?.customerUuid ?? null);
  const [dialogOwner, setDialogOwner] = useState<{
    customerUuid: typeof customerUuid;
    closeRequested: boolean;
    retiredInitialSlot?: ChosenSlot;
  }>({ customerUuid, closeRequested: false });
  const ownerChanged = customerUuid !== undefined && customerUuid !== dialogOwner.customerUuid;
  const retiringOwner = ownerChanged && dialogOwner.customerUuid != null;
  if (ownerChanged) {
    setDialogOwner({
      customerUuid,
      closeRequested: dialogOwner.closeRequested || (retiringOwner && isOpen),
      retiredInitialSlot: retiringOwner ? initialSlot : dialogOwner.retiredInitialSlot,
    });
  } else if (dialogOwner.closeRequested && !isOpen) {
    setDialogOwner({ ...dialogOwner, closeRequested: false });
  }
  const dialogOpen = isOpen && !retiringOwner && !dialogOwner.closeRequested;
  useEffect(() => {
    if (dialogOwner.closeRequested && isOpen) {
      onClose();
    }
  }, [dialogOwner.closeRequested, isOpen, onClose]);

  const gate = useWebTourBookingGate();
  const { query } = useRouter();
  const hasLoginReturn = query.tourDate != null && query.tourTime != null;
  const [hasMountedSheet, setHasMountedSheet] = useState(false);
  const canBookOnWeb = gate.isResolved && gate.isEnabled && Boolean(tourBookingListingId);
  const shouldMountSheet = hasMountedSheet || (canBookOnWeb && (dialogOpen || hasLoginReturn));
  if (shouldMountSheet && !hasMountedSheet) {
    setHasMountedSheet(true);
  }
  // The price card retains its chip selection until a new gesture replaces it.
  const ownedInitialSlot =
    retiringOwner || initialSlot === dialogOwner.retiredInitialSlot ? undefined : initialSlot;
  return (
    <>
      {/* Keep dispatched observers alive and let them observe auth changes across flag reads. */}
      {shouldMountSheet && (
        <TourBookingSheet
          isOpen={canBookOnWeb && dialogOpen}
          onClose={onClose}
          onOpenRequest={canBookOnWeb ? onOpenRequest : undefined}
          listingId={tourBookingListingId ?? null}
          addressToken={addressToken}
          home={home}
          initialSlot={ownedInitialSlot}
        />
      )}
      {!canBookOnWeb && (
        <SelfTourAppHandoffModal
          isOpen={dialogOpen}
          onClose={onClose}
          placement={placement}
          addressToken={addressToken}
          slug={slug}
        />
      )}
    </>
  );
};

/**
 * The flag decision between the two self-tour surfaces: the booking sheet when
 * buyer-web-tour-booking is on and the listing is web-bookable, the two-QR
 * app-handoff modal otherwise (the fallback and the path for buyers who want
 * the app).
 */
export const SelfTourDialog = (props: SelfTourDialogProps) => (
  <SelfTourQueryClientProvider>
    <SelfTourDialogSurface {...props} />
  </SelfTourQueryClientProvider>
);
